import { useMountEffect } from '@react-hookz/web'
import { noop, compact } from 'lodash-es'
import Fade from '@mui/material/Fade'
import CloseIcon from '@mui/icons-material/Close'
import type { ButtonProps } from 'settle-ui/components/Button/Button'
import { useSnackbarTimeout } from './hooks/useSnackbarTimeout/useSnackbarTimeout'
import * as $ from './Snackbar.styled'

export interface SnackbarButton extends Pick<ButtonProps, 'id' | 'sx' | 'disabled' | 'loading' | 'data-testid'> {
  onClick(): void
  text: string
}

interface Snackbar extends Pick<$.RootProps, 'id' | 'variant'> {
  onClose(): void
  timeout?: number
  onMount?(): void
  spinner?: boolean
  closable?: boolean
  permanent?: boolean
  retained?: boolean
  onCloseClick?(): void
  onMouseEnter?(): void
  onMouseLeave?(): void
  'data-testid'?: string
  message: React.ReactNode
  buttons?: Maybe<SnackbarButton>[]
}

export const Snackbar: React.FC<Snackbar> = ({
  message,
  timeout = 5000,
  closable = true,
  spinner,
  variant,
  buttons,
  permanent = false,
  retained = false,
  onMount = noop,
  onClose,
  onCloseClick,
  onMouseEnter,
  onMouseLeave,
  'data-testid': dataTestId,
  ...props
}) => {
  useMountEffect(onMount)

  useSnackbarTimeout(timeout, { permanent, retained, onClose })

  return (
    <Fade in={true}>
      <$.Root
        variant={variant}
        data-testid={`snackbar-${variant}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...props}
      >
        <$.Content data-testid={dataTestId}>
          {spinner && <$.Spinner color="inherit" size={18} />}
          <$.Message data-testid={dataTestId && `${dataTestId}-text`} variant="body2">
            {message}
          </$.Message>
          {compact(buttons).map(({ text, ...button }, i) => (
            <$.Button
              key={button.id ?? `button-${i}`}
              data-testid={dataTestId && `${dataTestId}-button`}
              size="small"
              {...button}
            >
              {text}
            </$.Button>
          ))}
          {closable && (
            <$.Close
              onClick={() => {
                onClose()
                onCloseClick?.()
              }}
              data-testid="close"
              color="inherit"
              size="small"
            >
              <CloseIcon data-testid="CloseIcon" />
            </$.Close>
          )}
        </$.Content>
      </$.Root>
    </Fade>
  )
}
